// ImprovedBasicAuth.js - Apache Basic Auth with Custom UI - FIXED
import React, { useState, useEffect } from "react"

class BasicAuthService {
  constructor() {
    this.credentialsKey = "basic_auth_credentials"

    // CORRETTO: Usa la stessa logica di api.js per determinare l'URL base
    const isProduction = true
    this.apiBase = isProduction ? "https://kosteaz.com/api" : "/api-proxy"
  }

  // Ottieni le credenziali salvate
  getSavedCredentials() {
    const saved = localStorage.getItem(this.credentialsKey)
    return saved ? JSON.parse(saved) : null
  }

  // Salva le credenziali per la sessione
  saveCredentials(username, password) {
    const credentials = {
      username,
      password,
      timestamp: Date.now(),
      encoded: btoa(`${username}:${password}`), // Base64 encoding per Basic Auth
    }
    localStorage.setItem(this.credentialsKey, JSON.stringify(credentials))
  }

  // Rimuovi le credenziali
  clearCredentials() {
    localStorage.removeItem(this.credentialsKey)
  }

  // Ottieni l'header Authorization per le richieste
  getAuthHeader() {
    const credentials = this.getSavedCredentials()
    return credentials ? `Basic ${credentials.encoded}` : null
  }

  // Testa se le credenziali sono valide
  async testCredentials(username, password) {
    const encoded = btoa(`${username}:${password}`)

    try {
      console.log(`Testing credentials against: ${this.apiBase}/auth.php`) // Debug

      // Testa con il nostro endpoint dedicato per l'autenticazione
      const response = await fetch(`${this.apiBase}/auth.php`, {
        method: "GET",
        headers: {
          Authorization: `Basic ${encoded}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })

      console.log(`Response status: ${response.status}`) // Debug

      // Prova a leggere la risposta JSON
      let responseData = {}
      try {
        responseData = await response.json()
      } catch (e) {
        console.warn("Could not parse JSON response:", e)
      }

      if (response.status === 401) {
        return {
          success: false,
          error: responseData.error || "Credenziali non valide",
        }
      } else if (response.status === 200 && responseData.authenticated) {
        return {
          success: true,
          message: responseData.message || "Login effettuato con successo",
        }
      } else {
        return {
          success: false,
          error: responseData.error || `Errore del server (${response.status})`,
        }
      }
    } catch (error) {
      console.error("Connection error:", error) // Debug
      return {
        success: false,
        error: `Errore di connessione: ${error.message}`,
      }
    }
  }

  // Controlla se l'utente è attualmente autenticato
  async isAuthenticated() {
    const credentials = this.getSavedCredentials()
    if (!credentials) return false

    // Testa le credenziali salvate
    const test = await this.testCredentials(
      credentials.username,
      credentials.password
    )
    if (!test.success) {
      this.clearCredentials()
      return false
    }

    return true
  }

  // Login con username e password
  async login(username, password) {
    const result = await this.testCredentials(username, password)

    if (result.success) {
      this.saveCredentials(username, password)
      return { success: true, message: "Login effettuato con successo" }
    } else {
      return result
    }
  }

  // Logout
  logout() {
    this.clearCredentials()

    // Force a page reload to clear any cached credentials in the browser
    setTimeout(() => {
      window.location.reload()
    }, 500)

    return { success: true, message: "Logout effettuato" }
  }
}

// Istanza globale del servizio
const basicAuthService = new BasicAuthService()

// Componente di Login Custom
const BasicAuthLogin = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!username.trim() || !password.trim()) {
      setError("Username e password sono obbligatori")
      return
    }

    setIsLoading(true)
    setError("")

    try {
      const result = await basicAuthService.login(username, password)

      if (result.success) {
        onLoginSuccess()
      } else {
        setError(result.error || "Errore durante il login")
      }
    } catch (error) {
      setError("Errore di connessione")
      console.error("Login error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-60 ay-flex ay-items-center ay-justify-center ay-z-50">
      <div className="ay-bg-white ay-rounded-xl ay-shadow-2xl ay-p-8 ay-w-full ay-max-w-md ay-mx-4">
        {/* Header */}
        <div className="ay-text-center ay-mb-8">
          <div className="ay-w-20 ay-h-20 ay-bg-gradient-to-br ay-from-blue-500 ay-to-purple-600 ay-rounded-full ay-flex ay-items-center ay-justify-center ay-mx-auto ay-mb-4">
            <svg
              className="ay-w-10 ay-h-10 ay-text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="ay-text-3xl ay-font-bold ay-text-gray-800 ay-mb-2">
            Admin Access
          </h2>
          <p className="ay-text-gray-600">
            Inserisci le tue credenziali per accedere al pannello amministrativo
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="ay-space-y-6">
          {/* Username */}
          <div>
            <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
              Username
            </label>
            <div className="ay-relative">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="ay-w-full ay-px-4 ay-py-3 ay-pl-10 ay-border ay-border-gray-300 ay-rounded-lg ay-focus:ay-ring-2 ay-focus:ay-ring-blue-500 ay-focus:ay-border-transparent ay-outline-none ay-transition"
                placeholder="Inserisci username"
                required
                autoFocus
              />
              <svg
                className="ay-w-5 ay-h-5 ay-text-gray-400 ay-absolute ay-left-3 ay-top-1/2 ay-transform ay--translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
              Password
            </label>
            <div className="ay-relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="ay-w-full ay-px-4 ay-py-3 ay-pl-10 ay-pr-10 ay-border ay-border-gray-300 ay-rounded-lg ay-focus:ay-ring-2 ay-focus:ay-ring-blue-500 ay-focus:ay-border-transparent ay-outline-none ay-transition"
                placeholder="Inserisci password"
                required
              />
              <svg
                className="ay-w-5 ay-h-5 ay-text-gray-400 ay-absolute ay-left-3 ay-top-1/2 ay-transform ay--translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="ay-absolute ay-right-3 ay-top-1/2 ay-transform ay--translate-y-1/2 ay-text-gray-400 ay-hover:ay-text-gray-600"
              >
                {showPassword ? (
                  <svg
                    className="ay-w-5 ay-h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464m1.414 1.414L18.536 18.536"
                    />
                  </svg>
                ) : (
                  <svg
                    className="ay-w-5 ay-h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Remember me */}
          <div className="ay-flex ay-items-center">
            <input
              id="remember"
              type="checkbox"
              defaultChecked={true}
              className="ay-h-4 ay-w-4 ay-text-blue-600 ay-focus:ay-ring-blue-500 ay-border-gray-300 ay-rounded"
            />
            <label
              htmlFor="remember"
              className="ay-ml-2 ay-block ay-text-sm ay-text-gray-700"
            >
              Ricorda le credenziali per questa sessione
            </label>
          </div>

          {/* Error message */}
          {error && (
            <div className="ay-bg-red-50 ay-border ay-border-red-200 ay-rounded-lg ay-p-4">
              <div className="ay-flex">
                <svg
                  className="ay-w-5 ay-h-5 ay-text-red-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="ay-ml-2 ay-text-sm ay-text-red-600">
                  {error}
                </span>
              </div>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading}
            className="ay-w-full ay-bg-gradient-to-r ay-from-blue-600 ay-to-purple-600 ay-text-white ay-py-3 ay-px-4 ay-rounded-lg ay-font-medium ay-hover:ay-from-blue-700 ay-hover:ay-to-purple-700 ay-focus:ay-ring-2 ay-focus:ay-ring-blue-500 ay-focus:ay-ring-offset-2 ay-disabled:ay-opacity-50 ay-disabled:ay-cursor-not-allowed ay-transition ay-duration-200 ay-transform ay-hover:ay-scale-105"
          >
            {isLoading ? (
              <div className="ay-flex ay-items-center ay-justify-center">
                <div className="ay-animate-spin ay-w-5 ay-h-5 ay-border-2 ay-border-white ay-border-t-transparent ay-rounded-full ay-mr-2"></div>
                Verifica in corso...
              </div>
            ) : (
              "Accedi al Pannello Admin"
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="ay-mt-6 ay-text-center">
          <p className="ay-text-xs ay-text-gray-500">
            Le credenziali vengono salvate localmente per questa sessione
          </p>
          <p className="ay-text-xs ay-text-gray-400 ay-mt-1">
            Connessione a: {basicAuthService.apiBase}
          </p>
        </div>
      </div>
    </div>
  )
}

// HOC per proteggere i componenti admin
const withBasicAuth = (WrappedComponent) => {
  return function BasicAuthProtectedComponent(props) {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
      const checkAuth = async () => {
        const authenticated = await basicAuthService.isAuthenticated()
        setIsAuthenticated(authenticated)
        setIsLoading(false)
      }

      checkAuth()
    }, [])

    const handleLoginSuccess = () => {
      setIsAuthenticated(true)
    }

    const handleLogout = () => {
      basicAuthService.logout()
      setIsAuthenticated(false)
    }

    if (isLoading) {
      return (
        <div className="ay-fixed ay-inset-0 ay-bg-white ay-flex ay-items-center ay-justify-center">
          <div className="ay-text-center">
            <div className="ay-animate-spin ay-w-12 ay-h-12 ay-border-4 ay-border-blue-500 ay-border-t-transparent ay-rounded-full ay-mx-auto"></div>
            <p className="ay-mt-4 ay-text-gray-600">Verificando l'accesso...</p>
          </div>
        </div>
      )
    }

    if (!isAuthenticated) {
      return <BasicAuthLogin onLoginSuccess={handleLoginSuccess} />
    }

    // Passa la funzione di logout al componente protetto
    return <WrappedComponent {...props} onLogout={handleLogout} />
  }
}

// Hook per usare l'autenticazione nei componenti
const useBasicAuth = () => {
  return {
    isAuthenticated: basicAuthService.getSavedCredentials() !== null,
    getAuthHeader: () => basicAuthService.getAuthHeader(),
    logout: () => basicAuthService.logout(),
    login: (username, password) => basicAuthService.login(username, password),
  }
}

export { basicAuthService, withBasicAuth, useBasicAuth, BasicAuthLogin }
export default basicAuthService
